import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import type { OpenHouse } from 'data/listing';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface OpenHouseDisplayData {
  label: string;
  isActive: boolean;
}

export interface OpenHouseFormatterOptions {
  locale: string;
  timezone: string;
  formatDateWithTemplate: (date: string | Date, template: string) => string;
  formatTime: (date: Date) => string;
}

export class OpenHouseFormatter {
  private locale: string;
  private userTimezone: string;
  private formatDateWithTemplate: (date: string | Date, template: string) => string;
  private formatTime: (date: Date) => string;

  constructor(options: OpenHouseFormatterOptions) {
    this.locale = options.locale;
    this.userTimezone = options.timezone;
    this.formatDateWithTemplate = options.formatDateWithTemplate;
    this.formatTime = options.formatTime;
  }

  format(openHouse: OpenHouse): OpenHouseDisplayData {
    return {
      label: this.formatLabel(openHouse),
      isActive: openHouse.isActive ?? true,
    };
  }

  formatAll(openHouses: OpenHouse[]): OpenHouseDisplayData[] {
    return openHouses.map(oh => this.format(oh));
  }

  private formatLabel(openHouse: OpenHouse): string {
    const normalized = this.normalizeForDisplay(openHouse);
    let label = this.formatDateWithTemplate(normalized.date, 'ddd MMM D');
    if (!normalized.from) return label;

    const timeIntervalStr = this.formatTimeInterval(normalized.from, normalized.to);
    label += ', ' + timeIntervalStr + (normalized.timezoneAbbreviation ? ' ' + normalized.timezoneAbbreviation : '');
    return label;
  }

  private normalizeForDisplay(openHouse: OpenHouse): {
    date: string | Date;
    from: string | null;
    to: string | null;
    timezoneAbbreviation: string;
  } {
    if (openHouse.timezone) {
      return this.normalizePropertyTimezoneOpenHouse(openHouse, openHouse.timezone);
    }

    return this.normalizeClientAppOpenHouse(openHouse);
  }

  private normalizePropertyTimezoneOpenHouse(openHouse: OpenHouse, propertyTimezone: string): {
    date: string;
    from: string | null;
    to: string | null;
    timezoneAbbreviation: string;
  } {
    let date = openHouse.date ?? '';
    let from = openHouse.from ? this.renderWallClockTime(openHouse.from) : null;
    let to = openHouse.to ? this.renderWallClockTime(openHouse.to) : null;

    const timezoneAbbreviation = this.timezoneAbbreviation(date, propertyTimezone);
    return { date, from, to, timezoneAbbreviation };
  }

  private normalizeClientAppOpenHouse(openHouse: OpenHouse): {
    date: string;
    from: string | null;
    to: string | null;
    timezoneAbbreviation: string;
  } {
    const timezoneAbbreviation = new Date()
      .toLocaleTimeString(this.locale, { timeZoneName: 'short' })
      .split(' ')
      .pop() || '';
    const date = openHouse.date ?? '';

    if (!openHouse.from) {
      return { date, from: null, to: null, timezoneAbbreviation };
    }

    const fromDate = this.parseOpenHouseTime(openHouse.date, openHouse.from);
    const fromDateInUserTz = dayjs.tz(fromDate, 'America/New_York').tz(this.userTimezone);
    const from = this.formatTime(fromDateInUserTz.toDate());

    let to: string | null = null;
    if (openHouse.to) {
      const toDate = this.parseOpenHouseTime(openHouse.date, openHouse.to);
      const toDateInUserTz = dayjs.tz(toDate, 'America/New_York').tz(this.userTimezone);
      to = this.formatTime(toDateInUserTz.toDate());
    }

    return { date, from, to, timezoneAbbreviation };
  }

  /**
   * Renders an "H:MMAM/PM" wall-clock string in the active locale.
   * English locales produce "2:00 PM"; French locales produce "14:00".
   * No timezone arithmetic — caller is responsible for ensuring the input
   * already represents the wall-clock time we want to display.
   */
  private renderWallClockTime(time: string): string {
    const match = time.match(/^(\d+):(\d+)(AM|PM)$/i);
    if (!match) return time;
    const [, hStr, minute, periodRaw] = match;
    const period = periodRaw.toUpperCase();
    let hour24 = parseInt(hStr, 10);
    if (period === 'PM' && hour24 !== 12) hour24 += 12;
    if (period === 'AM' && hour24 === 12) hour24 = 0;

    if (this.locale.startsWith('fr')) {
      return `${hour24}:${minute}`;
    }

    const hour12 = hour24 % 12 || 12;
    return `${hour12}:${minute} ${period}`;
  }

  /**
   * Computes the short timezone abbreviation (e.g. "EDT", "PST") for a given
   * IANA zone on the open-house date — DST-aware (an open house in November
   * returns "EST" while the same property in July returns "EDT").
   */
  private timezoneAbbreviation(date: string | null, propertyTz: string): string {
    const instant = date ? new Date(date) : new Date();
    const parts = new Intl.DateTimeFormat(this.locale, {
      timeZone: propertyTz,
      timeZoneName: 'short',
      hour: 'numeric',
    }).formatToParts(instant);
    return parts.find(p => p.type === 'timeZoneName')?.value ?? '';
  }

  private formatTimeInterval(fromTime: string, toTime: string | null | undefined): string {
    const isFrench = this.locale.startsWith('fr');

    if (isFrench) {
      if (toTime) {
        return `${fromTime} - ${toTime}`;
      }
      return fromTime;
    } else {
      const fromParts = fromTime.match(/^(.+?)\s*(AM|PM)$/i);
      const toParts = toTime?.match(/^(.+?)\s*(AM|PM)$/i);

      if (!toTime) {
        return fromTime;
      }

      if (fromParts && toParts && fromParts[2] === toParts[2]) {
        return `${fromParts[1]}-${toParts[1]} ${toParts[2]}`;
      } else {
        return `${fromTime} - ${toTime}`;
      }
    }
  }

  private parseOpenHouseTime(date: string | null, time: string): Date {
    if (!date) return new Date();

    const timeMatch = time.match(/^(\d+):(\d+)(AM|PM)$/);
    if (!timeMatch) {
      throw new Error('Invalid time format. Expected format: HH:MMAM/PM.');
    }

    const [, hours, minutes, period] = timeMatch;

    let hour = parseInt(hours, 10);
    if (period === 'PM' && hour !== 12) {
      hour += 12;
    } else if (period === 'AM' && hour === 12) {
      hour = 0;
    }

    const dateOnly = date.split('T')[0];
    const dateTimeString = `${dateOnly} ${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    const nyDate = dayjs.tz(dateTimeString, 'America/New_York');

    return nyDate.utc().toDate();
  }
}
